// src/ryan/components/product-blocks/FlashcardsDemoBlock.tsx
// Bloco de demonstração "Flashcards Adaptativos" — conteúdo editável pelo admin.
// Coreografia inspirada em src/components/landing/FlashcardsBlock.tsx (não importado, não alterado).

import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { DemoPillar, DemoClosing, DemoHeader, DemoPillarsList, DemoClosingPanel } from './DemoBlockShared';
import './FlashcardsDemoBlock.css';

// ── Tipos ──────────────────────────────────────────────────────────────

export interface FlashcardsDemoCard {
    id: string;
    front: string;
    back: string;
    tag: string;
}

export interface FlashcardsDemoData {
    label: string;
    headline: string;
    subheadline: string;
    cards: FlashcardsDemoCard[];
    pillars: DemoPillar[];
    closing: DemoClosing;
}

export const defaultFlashcardsDemoData = (): FlashcardsDemoData => ({
    label: 'Flashcards',
    headline: 'Nunca mais perca tempo escolhendo o que revisar.',
    subheadline: 'O algoritmo organiza automaticamente seus flashcards para que você estude primeiro aquilo que está prestes a esquecer.',
    cards: [
        {
            id: 'c1',
            front: 'Qual estrutura do coração atua como marcapasso fisiológico?',
            back: 'Nó sinoatrial (SA), localizado no átrio direito, responsável por iniciar o ritmo cardíaco.',
            tag: 'Sistema Cardiovascular',
        },
        {
            id: 'c2',
            front: 'Qual neurotransmissor está reduzido na doença de Parkinson?',
            back: 'Dopamina, devido à degeneração dos neurônios da substância negra pars compacta.',
            tag: 'Sistema Nervoso',
        },
        {
            id: 'c3',
            front: 'Qual é o anticorpo mais abundante no plasma humano?',
            back: 'IgG — responsável pela maior parte da imunidade humoral e capaz de atravessar a placenta.',
            tag: 'Imunologia',
        },
        {
            id: 'c4',
            front: 'Qual é a função principal da valva mitral?',
            back: 'Impedir o refluxo de sangue do ventrículo esquerdo para o átrio esquerdo durante a sístole.',
            tag: 'Sistema Cardiovascular',
        },
    ],
    pillars: [
        { id: 'pil-1', icon: 'Layers', title: 'Fila Inteligente', text: 'Cada card recebe prioridade dinâmica de acordo com o seu desempenho.' },
        { id: 'pil-2', icon: 'Zap', title: 'Adaptação Contínua', text: 'Cada resposta reorganiza automaticamente a sequência de estudo, mantendo o foco no que mais precisa da sua atenção.' },
        { id: 'pil-3', icon: 'ArrowRight', title: 'Domínio Progressivo', text: 'Conforme você evolui, o sistema reduz a frequência dos conteúdos dominados e prioriza aquilo que ainda precisa ser fortalecido.' },
    ],
    closing: {
        lead: 'Não é repetição.',
        emphasis: 'É reorganização inteligente do seu conhecimento.',
        items: [],
    },
});

const RATINGS = [
    { value: 1, label: 'Errei', color: '#dc2626', polarity: 'negative' },
    { value: 2, label: 'Difícil', color: '#ea580c', polarity: 'negative' },
    { value: 3, label: 'Ok', color: '#ca8a04', polarity: 'neutral' },
    { value: 4, label: 'Fácil', color: '#16a34a', polarity: 'positive' },
    { value: 5, label: 'Perfeito', color: '#0D3B0E', polarity: 'positive' },
] as const;

const MASTERY_DELTA: Record<number, number> = {
    1: -6, 2: -4, 3: 3, 4: 7, 5: 10,
};

const HOLD_MS = 120; // segura o rating "pressionado" antes de iniciar a troca de card

// Física da troca de card/flip (AnimatePresence, ver JSX) — um spring só,
// mola solta o bastante pra sentir "plástico" (leve acomodação no final) sem
// oscilar feito mola de verdade. Trocar por duration+easing fixos aqui volta
// a ficar com a cara "travada" que motivou essa reescrita.
const CARD_SPRING = { type: 'spring' as const, stiffness: 340, damping: 28, mass: 0.9 };

// ── Componente ───────────────────────────────────────────────────────

const FlashcardsDemoBlock: React.FC<{ data: FlashcardsDemoData }> = ({ data }) => {
    const wrapRef = useRef<HTMLDivElement>(null);
    const [visible, setVisible] = useState(false);

    const cards = data.cards.length > 0 ? data.cards : defaultFlashcardsDemoData().cards;

    const [cardIndex, setCardIndex] = useState(0);
    const [isFlipped, setIsFlipped] = useState(false);
    // Enquanto true: bloqueia novos cliques e mantém o header (tag/contador)
    // meio apagado. Ligado no clique da nota, desligado no onExitComplete do
    // AnimatePresence (ver JSX) — ou seja, no momento exato em que o card
    // antigo termina de sair, não num tempo fixo chutado à mão.
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [mastery, setMastery] = useState(42);
    const [masteryPulse, setMasteryPulse] = useState<'up' | 'down' | null>(null);
    const [pressedRating, setPressedRating] = useState<number | null>(null);

    // Altura fixa do card — sem isso, o container mede a altura pelo
    // conteúdo em tela (pergunta curta vs resposta longa, card atual vs
    // próximo) e ela muda a cada troca, empurrando o resto do bloco (e, por
    // causa do .rpb-demo-col-demo ser sticky, o layout em volta) pra cima/
    // baixo — o "sobe e desce" indesejado. Medindo a altura de TODAS as
    // faces (frente e verso de todos os cards) escondidas fora do fluxo e
    // travando o container nesse máximo, a altura nunca varia — só o
    // conteúdo interno anima (troca/flip).
    const measureRefs = useRef<(HTMLDivElement | null)[]>([]);
    const measureContainerRef = useRef<HTMLDivElement | null>(null);
    const [cardBoxHeight, setCardBoxHeight] = useState<number | null>(null);

    const measureCardHeights = useCallback(() => {
        const heights = measureRefs.current
            .filter((el): el is HTMLDivElement => !!el)
            .map(el => el.getBoundingClientRect().height);
        if (heights.length === 0) return;
        // .rpb-flk-card-measure-item mede só a altura natural de .rpb-flk-card-face
        // (que já inclui o padding DELA mesma). No render real, essa mesma face
        // ainda perde o padding vertical de .rpb-flk-card-face-layer (as duas
        // pontas, top+bottom) antes de receber height:100% — sem somar esse
        // padding aqui, .rpb-flk-card fica exatamente esses pixels mais baixo do
        // que o necessário, e a última linha da pergunta mais alta vaza por baixo
        // do card branco. Lendo o padding computado do próprio medidor (que já
        // reaplica o mesmo valor por causa da largura, ver CSS) em vez de um
        // número fixo, isso segue válido se o padding mudar no breakpoint mobile.
        const container = measureContainerRef.current;
        let verticalPad = 0;
        if (container) {
            const cs = window.getComputedStyle(container);
            verticalPad = parseFloat(cs.paddingTop || '0') + parseFloat(cs.paddingBottom || '0');
        }
        setCardBoxHeight(Math.max(...heights) + verticalPad);
    }, []);

    useLayoutEffect(() => {
        measureCardHeights();
    }, [cards, measureCardHeights]);

    useEffect(() => {
        let raf = 0;
        const onResize = () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(measureCardHeights);
        };
        window.addEventListener('resize', onResize);
        return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', onResize); };
    }, [measureCardHeights]);

    const shouldReduceMotion = useReducedMotion();

    const currentCard = cards[cardIndex % cards.length];

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.12 }
        );
        if (wrapRef.current) observer.observe(wrapRef.current);
        return () => observer.disconnect();
    }, []);

    const handleFlip = useCallback(() => {
        if (isTransitioning) return;
        setIsFlipped(true);
    }, [isTransitioning]);

    const handleAnswer = useCallback((rating: number) => {
        if (isTransitioning) return;

        setPressedRating(rating);
        setMastery(prev => Math.min(100, Math.max(0, prev + (MASTERY_DELTA[rating] ?? 0))));
        setIsTransitioning(true);

        // Segura o rating "pressionado" visível brevemente antes de trocar de
        // card — a troca em si (saída/entrada) é o AnimatePresence no JSX;
        // isTransitioning só volta a false no onExitComplete dele, ou seja, no
        // momento real em que a animação termina, não num tempo chutado aqui.
        setTimeout(() => {
            setCardIndex(prev => (prev + 1) % cards.length);
            setIsFlipped(false);
        }, shouldReduceMotion ? 0 : HOLD_MS);
    }, [isTransitioning, cards.length, shouldReduceMotion]);

    const prevMastery = useRef(mastery);
    useEffect(() => {
        if (prevMastery.current !== mastery) {
            const direction = mastery > prevMastery.current ? 'up' : 'down';
            setMasteryPulse(direction);
            const t = setTimeout(() => setMasteryPulse(null), 500);
            prevMastery.current = mastery;
            return () => clearTimeout(t);
        }
    }, [mastery]);

    const renderCardFace = (card: FlashcardsDemoCard, flipped: boolean) => (
        !flipped ? (
            <div className="rpb-flk-card-face rpb-flk-card-front">
                <p className="rpb-flk-card-face-hint">Pergunta</p>
                <p className="rpb-flk-card-question">{card.front}</p>
            </div>
        ) : (
            <div className="rpb-flk-card-face rpb-flk-card-back">
                <p className="rpb-flk-card-face-hint">Resposta</p>
                <p className="rpb-flk-card-answer">{card.back}</p>
            </div>
        )
    );

    return (
        <div className="rpb-demo-wrap" ref={wrapRef}>
            <DemoHeader label={data.label} headline={data.headline} subheadline={data.subheadline} visible={visible} />
            <div className="rpb-demo-grid">
                <div className="rpb-demo-col-demo">
                    <div className={`rpb-flk-shell ${visible ? 'rpb-flk-shell--visible' : ''}`}>
                        <div className="rpb-flk-header">
                            <div className="rpb-flk-header-left">
                                <span className="rpb-flk-logo">Graia Medicina</span>
                                <span className="rpb-flk-sep" />
                                <span className={`rpb-flk-mode ${isTransitioning ? 'rpb-flk-content--transitioning' : ''}`}>
                                    {currentCard.tag}
                                </span>
                            </div>
                            <span className={`rpb-flk-count${isTransitioning ? ' rpb-flk-count--swap' : ''}`}>
                                {(cardIndex % cards.length) + 1} / {cards.length}
                            </span>
                        </div>

                        <div className="rpb-flk-mastery-wrap">
                            <div className="rpb-flk-mastery-row">
                                <span className="rpb-flk-mastery-label">Domínio</span>
                                <span
                                    className={`rpb-flk-mastery-value ${masteryPulse === 'up' ? 'rpb-flk-mastery-value--up' :
                                        masteryPulse === 'down' ? 'rpb-flk-mastery-value--down' : ''
                                        }`}
                                >
                                    {mastery}%
                                </span>
                            </div>
                            <div className="rpb-flk-mastery-track">
                                <div
                                    className={`rpb-flk-mastery-fill ${masteryPulse === 'up' ? 'rpb-flk-mastery-fill--pulse-up' :
                                        masteryPulse === 'down' ? 'rpb-flk-mastery-fill--pulse-down' : ''
                                        }`}
                                    style={{ width: `${mastery}%` }}
                                />
                            </div>
                        </div>

                        <div
                            className="rpb-flk-card"
                            style={cardBoxHeight ? { height: `${cardBoxHeight}px` } : undefined}
                        >
                            {/* AnimatePresence troca a face (baralho ao avançar de card, ou
                                flip ao revelar a resposta) com física de mola em vez da
                                coreografia manual de antes — enquanto a face antiga ainda
                                está saindo, a nova já entra por cima, as duas coexistem no
                                DOM sozinhas (o framer-motion cuida disso). onExitComplete
                                dispara exatamente quando a saída termina, então é aí (não
                                num tempo chutado) que isTransitioning volta a false. */}
                            <AnimatePresence
                                initial={false}
                                onExitComplete={() => { setIsTransitioning(false); setPressedRating(null); }}
                            >
                                <motion.div
                                    key={`${currentCard.id}:${isFlipped ? 'back' : 'front'}`}
                                    className="rpb-flk-card-face-layer"
                                    initial={{ opacity: 0, x: 28, scale: 0.985 }}
                                    animate={{ opacity: 1, x: 0, scale: 1 }}
                                    exit={{ opacity: 0, x: -28, scale: 0.985 }}
                                    transition={shouldReduceMotion ? { duration: 0 } : CARD_SPRING}
                                >
                                    {renderCardFace(currentCard, isFlipped)}
                                </motion.div>
                            </AnimatePresence>
                            {/* Medidor invisível: todas as faces (frente/verso) de todos os
                                cards, fora do fluxo e escondidas, só pra calcular a altura
                                máxima real em measureCardHeights() — ver comentário acima. */}
                            <div className="rpb-flk-card-measure" aria-hidden="true" ref={measureContainerRef}>
                                {cards.map((card, i) => (
                                    <React.Fragment key={card.id}>
                                        <div
                                            className="rpb-flk-card-measure-item"
                                            ref={el => { measureRefs.current[i * 2] = el; }}
                                        >
                                            {renderCardFace(card, false)}
                                        </div>
                                        <div
                                            className="rpb-flk-card-measure-item"
                                            ref={el => { measureRefs.current[i * 2 + 1] = el; }}
                                        >
                                            {renderCardFace(card, true)}
                                        </div>
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>

                        <div className={`rpb-flk-actions ${isTransitioning ? 'rpb-flk-content--transitioning' : ''}`}>
                            {!isFlipped ? (
                                <button className="rpb-flk-btn-reveal" onClick={handleFlip} disabled={isTransitioning}>
                                    Ver resposta
                                </button>
                            ) : (
                                <div className="rpb-flk-ratings">
                                    <div className="rpb-flk-ratings-row">
                                        {RATINGS.map(r => (
                                            <button
                                                key={r.value}
                                                className={[
                                                    'rpb-flk-rating-btn',
                                                    `rpb-flk-rating-btn--${r.polarity}`,
                                                    pressedRating === r.value ? 'rpb-flk-rating-btn--pressed' : '',
                                                ].filter(Boolean).join(' ')}
                                                style={{ '--rpb-flk-rating-color': r.color } as React.CSSProperties}
                                                onClick={() => handleAnswer(r.value)}
                                                disabled={isTransitioning}
                                            >
                                                <span className="rpb-flk-rating-num">{r.value}</span>
                                                <span className="rpb-flk-rating-text">{r.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="rpb-demo-col-pillars">
                    <DemoPillarsList pillars={data.pillars} visible={visible} />
                </div>
            </div>
            <DemoClosingPanel closing={data.closing} visible={visible} />
        </div>
    );
};

export default FlashcardsDemoBlock;
